import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import {
    DisplayMessageMinimum,
    IGenericResponse,
    IModelBase,
    IStatusMessage,
    ResponseStatus
} from '../../common/Interfaces';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromErrors from './store';

// TODO: make this into a module so we can inject store, etc

const log = new LoggingService();
export const wait = delay => new Promise(resolve => setTimeout(resolve, delay));
export const filterErrors = (i, p, o) => i && o.indexOf(i) === p;
export const IsSuccessStatus = (statusCode: ResponseStatus) =>
    statusCode === ResponseStatus.Success ||
    statusCode === ResponseStatus.Warning ||
    statusCode === ResponseStatus.PartialSuccess;

export function MapItemsToStatus<
    T extends {
        id?: string | number;
        processId?: number;
        Status?: IStatusMessage;
    }
>(items: T[]): IStatusMessage[] {
    return items
        .filter(
            x =>
                !(
                    x.Status.StatusCode === ResponseStatus.Success ||
                    x.Status.StatusCode === ResponseStatus.None
                )
        )
        .map(x => x.Status);
}
export function MapErrorsToStatus<
    T extends {
        id?: string | number;
        processId?: number;
        Status?: IStatusMessage;
    }
>(resp: IGenericResponse<T>): IStatusMessage[] {
    const payload: IGenericResponse<T> =
        resp.Items && resp.Items.length
            ? resp
            : ({
                  ...resp,
                  Items: [
                      {
                          id: resp.id,
                          Status: {
                              MsgProcessID: resp.processId,
                              MsgEntityID: resp.id,
                              StatusCode: resp.StatusCode,
                              Message:
                                  resp.ErrorMessages.reduce(
                                      (a, c) => `${c}\n${a}`,
                                      ''
                                  ) +
                                  resp.WarningMessages.reduce(
                                      (a, c) => `${c}\n${a}`,
                                      ''
                                  )
                          } as IStatusMessage
                      }
                  ]
              } as IGenericResponse<T>);
    log.verbose({ type: 'util:MapErrorsToStatus', resp, payload });
    const messages = payload.Items.filter(
        x => x && x.Status && x.Status.StatusCode >= DisplayMessageMinimum
    );
    log.verbose({ type: 'util:MapErrorsToStatus', messages });

    let result =
        messages && messages.length > 0
            ? [...messages.map(x => x.Status)]
            : [
                  ...getResponseErrors(resp)
                      .map(
                          x =>
                              ({
                                  StatusCode: resp.StatusCode,
                                  Message: x,
                                  MsgProcessID: 0,
                                  MsgEntityID: ''
                              } as IStatusMessage)
                      )
                      .filter(x => x.StatusCode >= DisplayMessageMinimum)
              ];
    result = result.filter(filterErrors);
    // const result = [
    //     ...getResponseErrors(resp).map(
    //         x =>
    //             ({
    //                 StatusCode: resp.StatusCode,
    //                 Message: x,
    //                 MsgProcessID: 0,
    //                 MsgEntityID: ''
    //             } as IStatusMessage)
    //     ),
    //     ...resp.Items.filter(
    //         x => x && x.Status && x.Status.StatusCode >= DisplayMessageMinimum
    //     ).map(x => x.Status)
    //     // ({StatusCode: payload.StatusCode, Message: payload.StatusType, MsgProcessID: resp.processId, MsgEntityID: resp.id})
    // ].filter(filterErrors);
    log.verbose({ type: 'util:MapErrorsToStatus', resp, payload, result });
    return result;
}

export function getResponseErrors<T extends IModelBase>(
    resp: IGenericResponse<T>
): string[] {
    return [
        ...(resp.ErrorMessages && resp.ErrorMessages.length
            ? resp.ErrorMessages
            : [resp.StatusType])
    ].filter(
        e =>
            resp.Items.findIndex(x => x && x.Status && x.Status.Message === e) <
            0
    );
}
/**
 * Recursively finds the most deeply nested error property
 * Traverses common error property names: error, Error, InnerException, ExceptionMessage, message, Message
 */
function getDeepestError(error: any, depth: number = 0, maxDepth: number = 10): any {
    if (!error || depth >= maxDepth) {
        return error;
    }

    // Common error property names to check for nested error objects
    const errorProps = ['error', 'Error', 'InnerException', 'innerException'];
    const messageProps = ['ExceptionMessage', 'exceptionMessage', 'message', 'Message', 'statusText'];

    // First, try to find nested error objects and recurse into them
    for (const prop of errorProps) {
        if (error[prop] && typeof error[prop] === 'object' && !Array.isArray(error[prop])) {
            const nested = getDeepestError(error[prop], depth + 1, maxDepth);
            // If we found a deeper error with a message, return it
            if (nested) {
                // Check if nested has a message property
                const hasMessage = messageProps.some(msgProp => 
                    nested[msgProp] && typeof nested[msgProp] === 'string' && nested[msgProp].trim()
                );
                if (hasMessage || typeof nested === 'string') {
                    return nested;
                }
            }
        }
    }

    // If no deeper nested errors found, check if current error has a message
    for (const prop of messageProps) {
        if (error[prop] && typeof error[prop] === 'string' && error[prop].trim()) {
            return error;
        }
    }

    // If error itself is a string, return it
    if (typeof error === 'string') {
        return error;
    }

    // Return the current error if no deeper nested errors found
    return error;
}

export function handleDataServiceError<T>(
    store: Store,
    e,
    c: Observable<T>
): Observable<T> {
    let error;
    if (e instanceof Function) {
        error = e();
    } else {
        error = e;
    }
    if (
        !(
            (error && error.error && error.error.status === 401) ||
            (error && error.status === 401)
        )
    ) {
        log.error({ type: 'handleDataServiceError', error, c });
        // Extract the most deeply nested error before passing to getErrorAction
        const deepestError = getDeepestError(error);
        store.dispatch(getErrorAction(deepestError));
    }
    throw error;
}
export function getErrorAction(e): Action {
    return new fromErrors.SetErrors([
        {
            StatusCode: ResponseStatus.Failed,
            Message: `${
                e &&
                (typeof e.error == 'string'
                    ? e.error
                    : (e.error &&
                          e.error.InnerException &&
                          e.error.InnerException.ExceptionMessage) ||
                      (e?.error?.error && e.error.error.ExceptionMessage) ||
                      (e?.error?.error && e.error.error.message) ||
                      (e.error && e.error.ExceptionMessage) ||
                      (e.Error && e.Error.message) ||
                      (e.Error && e.Error.Message) ||
                      (e.error && e.error.message) ||
                      (e.error && e.error.Message) ||
                      (e.error && e.error.statusText) ||
                      e.message ||
                      e.ExceptionMessage ||
                      e.Message ||
                      e.statusText ||
                      e)
            }`,
            ...e
        }
    ]);
}
export function handleServiceError(
    e: any,
    c: Observable<any>
): Observable<Action> {
    let error;
    if (e instanceof Function) {
        error = e();
    } else {
        error = e;
    }
    log.error({ type: 'handleServiceError', error });
    if (error.status === 401) {
        return EMPTY;
    }
    return of(getErrorAction(error));
}

export function handleNullDataError(data, service): Action {
    return new fromErrors.SetErrors([
        {
            StatusCode: ResponseStatus.Failed,
            Message: `${service} failed to load data`
        }
    ]);
}

const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_BACKOFF = 1000;
const errorMessage = (maxRetry: number) =>
    `tried to load resource ${maxRetry} times with failure, giving up.`;

// use whats in 'backoff-rxjs' instead?
// export function delayedRetry(
//     delayMs,
//     maxRetry
// ): <T>(source: Observable<T>) => Observable<T> {
//     let retries = maxRetry;

//     return <T>(src: Observable<T>) =>
//         src.pipe(
//             retryWhen<T>((errors: Observable<T>) =>
//                 errors.pipe(
//                     delay(delayMs),
//                     mergeMap(error =>
//                         retries-- > 0
//                             ? of(error)
//                             : throwError(errorMessage(maxRetry))
//                     )
//                 )
//             )
//         );
// }

// export function delayedRetryWithBackoff(
//     delayMs: number = 1000,
//     maxRetry = DEFAULT_MAX_RETRIES,
//     backoffMs = DEFAULT_BACKOFF
// ): <T>(source: Observable<T>) => Observable<T> {
//     let retries = maxRetry;

//     return <T>(src: Observable<any>) =>
//         src.pipe(
//             retryWhen<T>((errors: Observable<T>) =>
//                 errors.pipe(
//                     mergeMap(error => {
//                         if (retries-- > 0) {
//                             const backoffTime =
//                                 delayMs + (maxRetry - retries) * backoffMs;
//                             return of(error).pipe(delay(backoffTime));
//                         }
//                         return throwError(errorMessage(maxRetry));
//                     })
//                 )
//             )
//         );
// }
